import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, setDoc } from 'firebase/firestore';
import { Trash2, PlusCircle, Package, ShoppingBag, Mail, Phone, MapPin, Database, Search, Edit3, X, Image as ImageIcon } from 'lucide-react';

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('products'); // 'products' or 'orders'
  
  // Product states
  const [products, setProducts] = useState([]);
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [mrp, setMrp] = useState('');
  const [category, setCategory] = useState('Shoes');
  const [subCategory, setSubCategory] = useState('Running Shoes');
  const [image, setImage] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);

  // Search & Filter & Pagination states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 8;

  // Edit Product Modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentEditId, setCurrentEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [editMrp, setEditMrp] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editSubCategory, setEditSubCategory] = useState('');
  const [editImage, setEditImage] = useState('');
  const [editDescription, setEditDescription] = useState('');

  // Orders states
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  // Fetch products from Firebase Firestore
  const fetchProducts = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'products'));
      const productList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProducts(productList);
    } catch (error) {
      console.error("Error fetching products: ", error);
    }
  };

  // Fetch orders from Firebase Firestore
  const fetchOrders = async () => {
    setOrdersLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'orders'));
      const orderList = querySnapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      orderList.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setOrders(orderList);
    } catch (error) {
      console.error("Error fetching orders: ", error);
    } finally {
      setOrdersLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchOrders();
  }, []);

  // Add new product to Firebase
  const handleAddProduct = async (e) => {
    e.preventDefault();
    if (!name || !price) {
      alert("Please fill in all required fields!");
      return;
    }

    setLoading(true);
    try {
      await addDoc(collection(db, 'products'), {
        name,
        price: Number(price),
        mrp: mrp ? Number(mrp) : Number(price) + 500,
        category,
        subCategory,
        image: image || 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=500',
        description,
        rating: 4.5,
        reviewsCount: 120,
        createdAt: new Date()
      });
      setName('');
      setPrice('');
      setMrp('');
      setImage('');
      setDescription('');
      fetchProducts();
      alert("Product added successfully to database!");
    } catch (error) {
      console.error("Error adding product: ", error);
      alert("Failed to add product.");
    } finally {
      setLoading(false);
    }
  };

  // Open Edit Modal
  const handleOpenEdit = (product) => {
    setCurrentEditId(product.id);
    setEditName(product.name || '');
    setEditPrice(product.price || '');
    setEditMrp(product.mrp || '');
    setEditCategory(product.category || 'Shoes');
    setEditSubCategory(product.subCategory || '');
    setEditImage(product.image || '');
    setEditDescription(product.description || '');
    setIsEditModalOpen(true);
  };

  // Update Product Handler
  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    if (!editName || !editPrice) {
      alert("Name and Price are required!");
      return;
    }

    try {
      const productRef = doc(db, 'products', currentEditId);
      await updateDoc(productRef, {
        name: editName,
        price: Number(editPrice),
        mrp: Number(editMrp),
        category: editCategory,
        subCategory: editSubCategory,
        image: editImage,
        description: editDescription
      });
      setIsEditModalOpen(false);
      fetchProducts();
      alert("Product updated successfully!");
    } catch (error) {
      console.error("Error updating product: ", error);
      alert("Failed to update product.");
    }
  };

  // 1-Click Structured Database Seeder Handler (Shoes & E-commerce Style)
  const handleSeedDatabase = async () => {
    if (!window.confirm("Are you sure you want to seed structured store items into Firestore?")) {
      return;
    }

    setSeeding(true);
    try {
      const categoryStructure = {
        "Shoes": ["Gym Shoes", "Running Shoes", "Performance Shoes", "Walking Shoes", "Sports"],
        "Stationery": ["Notebooks", "Pens", "Pencils", "Highlighters"],
        "Home Decor": ["Wall Hanging", "Aroma Diffuser", "Plant Planter"]
      };

      const adjectivesList = ["Avant", "Campus", "Bacca Bucci", "Sparx", "Puma", "Nike", "Reebok", "Classic"];
      const sampleImages = [
        "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=500",
        "https://images.unsplash.com/photo-1607522370275-f14206abe5d3?w=500",
        "https://images.unsplash.com/photo-1595950653106-6c9ebd614d3a?w=500",
        "https://images.unsplash.com/photo-1533867617858-e7d97e0afd8b?w=500",
        "https://images.unsplash.com/photo-1525966222134-fcfa99b8ae77?w=500"
      ];

      let globalCount = 1;

      for (const [mainCategory, subCategories] of Object.entries(categoryStructure)) {
        for (const subCat of subCategories) {
          for (let i = 1; i <= 3; i++) {
            const brand = adjectivesList[globalCount % adjectivesList.length];
            const productId = `${mainCategory.toLowerCase()}-${subCat.toLowerCase().replace(/\s+/g, '-')}-${i}`;
            const productName = `${brand} Men's ${subCat}`;
            
            const productData = {
              name: productName,
              price: Math.floor(Math.random() * (2500 - 800 + 1)) + 800,
              mrp: 3999,
              rating: +(Math.random() * (5.0 - 4.2) + 4.2).toFixed(1),
              reviewsCount: Math.floor(Math.random() * 300) + 20,
              category: mainCategory,
              subCategory: subCat,
              image: sampleImages[globalCount % sampleImages.length],
              description: `Designed for optimal airflow and high durability. Perfect for daily fitness and casual wear.`,
              createdAt: new Date()
            };

            await setDoc(doc(db, "products", productId), productData);
            globalCount++;
          }
        }
      }

      fetchProducts();
      alert("Success! E-commerce catalog layout successfully seeded to Firebase!");
    } catch (error) {
      console.error("Error seeding products: ", error);
      alert("Failed to seed database.");
    } finally {
      setSeeding(false);
    }
  };

  // Delete product from Firebase
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        await deleteDoc(doc(db, 'products', id));
        fetchProducts();
      } catch (error) {
        console.error("Error deleting product: ", error);
      }
    }
  };

  // Update Order Status Handler
  const handleStatusChange = async (orderId, newStatus) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, { status: newStatus });
      setOrders(orders.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
    } catch (error) {
      console.error("Error updating order status: ", error);
      alert("Failed to update status.");
    }
  };

  // Filtered Products Logic
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          (product.category && product.category.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = filterCategory === 'All' || product.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  // Pagination Logic
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        
        {/* Header & Tab Switcher */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div className="flex items-center space-x-3">
            <Package className="h-8 w-8 text-indigo-600" />
            <h1 className="text-3xl font-extrabold text-gray-900">Admin Control Panel</h1>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleSeedDatabase}
              disabled={seeding}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold shadow transition-colors flex items-center space-x-2"
            >
              <Database className="h-4 w-4" />
              <span>{seeding ? 'Seeding Catalog...' : '🚀 Seed E-Commerce Catalog'}</span>
            </button>

            <div className="flex space-x-2 bg-white p-1 rounded-xl shadow-xs border border-gray-200">
              <button
                onClick={() => setActiveTab('products')}
                className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                  activeTab === 'products' ? 'bg-indigo-600 text-white shadow' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Manage Products ({products.length})
              </button>
              <button
                onClick={() => setActiveTab('orders')}
                className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                  activeTab === 'orders' ? 'bg-indigo-600 text-white shadow' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Customer Orders ({orders.length})
              </button>
            </div>
          </div>
        </div>

        {/* TAB 1: PRODUCTS MANAGEMENT */}
        {activeTab === 'products' && (
          <div>
            {/* Add Product Form */}
            <div className="bg-white p-6 rounded-xl shadow-md mb-10 border border-gray-100">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center space-x-2">
                <PlusCircle className="h-5 w-5 text-indigo-600" />
                <span>Add New Store Product</span>
              </h2>
              
              <form onSubmit={handleAddProduct} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Product Name</label>
                  <input 
                    type="text" 
                    value={name} 
                    onChange={(e) => setName(e.target.value)} 
                    required 
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="e.g. Avant Men's Running Shoes"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Price (₹)</label>
                  <input 
                    type="number" 
                    value={price} 
                    onChange={(e) => setPrice(e.target.value)} 
                    required 
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="e.g. 1499"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">MRP (₹)</label>
                  <input 
                    type="number" 
                    value={mrp} 
                    onChange={(e) => setMrp(e.target.value)} 
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="e.g. 3999"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Category</label>
                  <select 
                    value={category} 
                    onChange={(e) => setCategory(e.target.value)}
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="Shoes">Shoes</option>
                    <option value="Stationery">Stationery</option>
                    <option value="Home Decor">Home Decor</option>
                    <option value="Biodegradable">Biodegradable</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Sub-Category</label>
                  <input 
                    type="text" 
                    value={subCategory} 
                    onChange={(e) => setSubCategory(e.target.value)}
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="e.g. Running Shoes / Gym Shoes"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Image URL</label>
                  <input 
                    type="text" 
                    value={image} 
                    onChange={(e) => setImage(e.target.value)}
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="https://images.unsplash.com/..."
                  />
                </div>

                <div className="sm:col-span-3">
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <input 
                    type="text" 
                    value={description} 
                    onChange={(e) => setDescription(e.target.value)}
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Designed for optimal airflow..."
                  />
                </div>

                <div className="sm:col-span-3">
                  <button 
                    type="submit" 
                    disabled={loading}
                    className="w-full bg-indigo-600 text-white py-2.5 rounded-md font-medium hover:bg-indigo-700 transition-colors shadow"
                  >
                    {loading ? 'Saving to Database...' : 'Add Product to Store'}
                  </button>
                </div>
              </form>
            </div>

            {/* Existing Products Inventory List */}
            <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                <h2 className="text-xl font-bold text-gray-800">Live Database Inventory</h2>
                
                {/* Search & Filter Controls */}
                <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
                  <div className="relative flex-1 sm:flex-initial">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <input 
                      type="text"
                      placeholder="Search products..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 pr-4 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 w-full"
                    />
                  </div>

                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    <option value="All">All Categories</option>
                    <option value="Shoes">Shoes</option>
                    <option value="Stationery">Stationery</option>
                    <option value="Home Decor">Home Decor</option>
                    <option value="Biodegradable">Biodegradable</option>
                  </select>
                </div>
              </div>
              
              {currentProducts.length === 0 ? (
                <div className="text-center py-10 space-y-3">
                  <p className="text-gray-500 text-sm">No products found matching your filter.</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Image</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {currentProducts.map((product) => (
                          <tr key={product.id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <img 
                                src={product.image || 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=500'} 
                                alt={product.name} 
                                className="h-10 w-10 object-cover rounded-lg border border-gray-200"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {product.name}
                              {product.subCategory && <span className="block text-xs text-gray-500">{product.subCategory}</span>}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{product.category}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                              ₹{product.price} {product.mrp && <span className="text-xs text-gray-400 line-through font-normal">₹{product.mrp}</span>}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                              <button 
                                onClick={() => handleOpenEdit(product)}
                                className="text-indigo-600 hover:text-indigo-900 p-1 bg-indigo-50 rounded"
                                title="Edit Product"
                              >
                                <Edit3 className="h-4 w-4" />
                              </button>
                              <button 
                                onClick={() => handleDelete(product.id)}
                                className="text-red-600 hover:text-red-900 p-1 bg-red-50 rounded"
                                title="Delete Product"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200">
                      <span className="text-xs text-gray-500">
                        Page {currentPage} of {totalPages}
                      </span>
                      <div className="space-x-2">
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                          className="px-3 py-1 bg-gray-100 rounded text-xs font-bold disabled:opacity-50"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                          disabled={currentPage === totalPages}
                          className="px-3 py-1 bg-gray-100 rounded text-xs font-bold disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* TAB 2: CUSTOMER ORDERS MANAGEMENT */}
        {activeTab === 'orders' && (
          <div className="space-y-6">
            {ordersLoading ? (
              <p className="text-center text-gray-500 py-16">Loading customer orders...</p>
            ) : orders.length === 0 ? (
              <div className="bg-white p-12 rounded-xl text-center shadow-sm border border-gray-200">
                <ShoppingBag className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <h3 className="font-bold text-gray-800 text-lg mb-1">No customer orders yet</h3>
                <p className="text-sm text-gray-500">Orders placed from checkout will appear here instantly.</p>
              </div>
            ) : (
              orders.map((order) => {
                const details = order.customerDetails || {};
                return (
                  <div key={order.id} className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                    
                    {/* Order Bar */}
                    <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                      <div>
                        <span className="text-xs text-gray-500 font-semibold">ORDER ID: </span>
                        <span className="font-mono text-xs text-gray-900 font-bold">{order.id}</span>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {order.createdAt?.seconds ? new Date(order.createdAt.seconds * 1000).toLocaleString() : 'Just now'}
                        </div>
                      </div>

                      <div className="flex items-center space-x-3">
                        <span className="text-xs font-semibold text-gray-600">Status:</span>
                        <select 
                          value={order.status || 'Processing'} 
                          onChange={(e) => handleStatusChange(order.id, e.target.value)}
                          className="bg-white border border-gray-300 rounded-lg px-3 py-1.5 text-xs font-bold text-indigo-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        >
                          <option value="Processing">Processing</option>
                          <option value="Shipped">Shipped</option>
                          <option value="Delivered">Delivered</option>
                          <option value="Cancelled">Cancelled</option>
                        </select>
                      </div>
                    </div>

                    {/* Customer Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 border-b border-gray-100 bg-white text-xs">
                      <div>
                        <h4 className="font-bold text-gray-700 uppercase tracking-wider mb-2">Customer Info</h4>
                        <p className="font-bold text-gray-900 text-sm">{details.fullName || 'N/A'}</p>
                        <p className="text-gray-500 flex items-center space-x-1 mt-1">
                          <Mail className="h-3 w-3 inline" />
                          <span>{details.email || 'N/A'}</span>
                        </p>
                        <p className="text-gray-500 flex items-center space-x-1 mt-0.5">
                          <Phone className="h-3 w-3 inline" />
                          <span>{details.phone || 'N/A'}</span>
                        </p>
                      </div>

                      <div>
                        <h4 className="font-bold text-gray-700 uppercase tracking-wider mb-2">Shipping Address</h4>
                        <p className="text-gray-600 flex items-start space-x-1">
                          <MapPin className="h-3.5 w-3.5 text-indigo-600 flex-shrink-0 mt-0.5" />
                          <span>{details.address}, {details.city} - {details.pincode}</span>
                        </p>
                      </div>
                    </div>

                    {/* Items */}
                    <div className="p-6 bg-gray-50/50">
                      <h4 className="font-bold text-gray-700 uppercase tracking-wider mb-3 text-xs">Purchased Items</h4>
                      <div className="space-y-2">
                        {order.items?.map((item, idx) => (
                          <div key={idx} className="flex justify-between items-center text-xs bg-white p-3 rounded-lg border border-gray-200">
                            <span className="font-bold text-gray-800">{item.name} <span className="text-gray-500 font-normal">(Qty: {item.quantity})</span></span>
                            <span className="font-bold text-gray-900">₹{item.price * item.quantity}</span>
                          </div>
                        ))}
                      </div>

                      <div className="flex justify-between items-center mt-4 pt-3 border-t border-gray-200 text-sm font-extrabold text-gray-900">
                        <span>Grand Total</span>
                        <span className="text-indigo-600 text-base">₹{order.total}</span>
                      </div>
                    </div>

                  </div>
                );
              })
            )}
          </div>
        )}

      </div>

      {/* EDIT PRODUCT MODAL */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 relative max-h-[90vh] overflow-y-auto">
            <button 
              onClick={() => setIsEditModalOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>

            <h3 className="text-xl font-bold text-gray-800 mb-4">Edit Product</h3>

            <form onSubmit={handleUpdateProduct} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Product Name</label>
                <input 
                  type="text" 
                  value={editName} 
                  onChange={(e) => setEditName(e.target.value)} 
                  required 
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Price (₹)</label>
                  <input 
                    type="number" 
                    value={editPrice} 
                    onChange={(e) => setEditPrice(e.target.value)} 
                    required 
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">MRP (₹)</label>
                  <input 
                    type="number" 
                    value={editMrp} 
                    onChange={(e) => setEditMrp(e.target.value)} 
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Category</label>
                  <select 
                    value={editCategory} 
                    onChange={(e) => setEditCategory(e.target.value)}
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="Shoes">Shoes</option>
                    <option value="Stationery">Stationery</option>
                    <option value="Home Decor">Home Decor</option>
                    <option value="Biodegradable">Biodegradable</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Sub-Category</label>
                  <input 
                    type="text" 
                    value={editSubCategory} 
                    onChange={(e) => setEditSubCategory(e.target.value)} 
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Image URL</label>
                <input 
                  type="text" 
                  value={editImage} 
                  onChange={(e) => setEditImage(e.target.value)} 
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <input 
                  type="text" 
                  value={editDescription} 
                  onChange={(e) => setEditDescription(e.target.value)}
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 bg-gray-100 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}